import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { FiRepeat } from 'react-icons/fi';
import Card from '../../../components/Card';
import { useLanguage } from '../../../i18n/LanguageContext';
import apiUrl from '../../../config/api';
import ToolShell from './ToolShell';
import useArchiveSubjects from './useArchiveSubjects';

const reasonVariant = {
  type: 'warning',
  'correct-answer': 'danger',
  answers: 'info',
};

const AnswerList = ({ title, answers }) => (
  <div className="quizify-compare-answer-list">
    <strong>{title}</strong>
    {answers?.length ? (
      <ul>
        {answers.map((answer, index) => (
          <li key={`${answer.text}-${index}`} className={answer.isCorrect ? 'is-correct' : ''}>
            {answer.text}
          </li>
        ))}
      </ul>
    ) : (
      <span>-</span>
    )}
  </div>
);

const QuestionOnlyList = ({ questions, emptyText, answersTitle }) => {
  if (!questions.length) {
    return <div className="quizify-tool-empty compact">{emptyText}</div>;
  }

  return (
    <div className="quizify-compare-only-list">
      {questions.map((question) => (
        <div key={question.id} className="quizify-compare-question-card">
          <h5>{question.questionText}</h5>
          <AnswerList title={answersTitle} answers={question.answers} />
        </div>
      ))}
    </div>
  );
};

const SubjectCompareTool = () => {
  const { t } = useLanguage();
  const text = t.toolsPage.subjectCompare;
  const {
    token,
    authHeaders,
    archives,
    subjectsByArchiveId,
    isLoadingArchives,
    loadingSubjectArchiveId,
    error: loadError,
    setError: setLoadError,
    loadSubjects,
  } = useArchiveSubjects();
  const [baseArchiveId, setBaseArchiveId] = useState('');
  const [compareArchiveId, setCompareArchiveId] = useState('');
  const [baseSubjectId, setBaseSubjectId] = useState('');
  const [compareSubjectId, setCompareSubjectId] = useState('');
  const [comparison, setComparison] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [error, setError] = useState('');

  const baseSubjects = useMemo(() => subjectsByArchiveId[baseArchiveId] || [], [baseArchiveId, subjectsByArchiveId]);
  const compareSubjects = useMemo(() => subjectsByArchiveId[compareArchiveId] || [], [compareArchiveId, subjectsByArchiveId]);
  const canCompare = Boolean(baseSubjectId && compareSubjectId && baseSubjectId !== compareSubjectId && token);

  useEffect(() => {
    if (baseArchiveId) {
      loadSubjects(baseArchiveId);
    }
  }, [baseArchiveId, loadSubjects]);

  useEffect(() => {
    if (compareArchiveId) {
      loadSubjects(compareArchiveId);
    }
  }, [compareArchiveId, loadSubjects]);

  const handleCompare = async (event) => {
    event.preventDefault();
    setError('');
    setLoadError('');
    setComparison(null);

    if (!canCompare) {
      setError(text.chooseSubjects);
      return;
    }

    setIsComparing(true);

    try {
      const response = await axios.post(
        `${apiUrl}/tools/compareSubjects`,
        { baseSubjectId, compareSubjectId },
        { headers: authHeaders }
      );

      setComparison(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || text.error);
    } finally {
      setIsComparing(false);
    }
  };

  const handleBaseArchiveChange = (event) => {
    setBaseArchiveId(event.target.value);
    setBaseSubjectId('');
    setComparison(null);
  };

  const handleCompareArchiveChange = (event) => {
    setCompareArchiveId(event.target.value);
    setCompareSubjectId('');
    setComparison(null);
  };

  const reasonLabel = (reason) => text.reasons[reason] || reason;

  return (
    <ToolShell
      title={text.title}
      subtitle={text.subtitle}
      icon={<FiRepeat aria-hidden="true" />}
    >
      <Row className="g-3">
        <Col lg={4}>
          <Card className="quizify-tool-panel">
            <Card.Body>
              {(error || loadError) && (
                <Alert variant="danger" dismissible onClose={() => {
                  setError('');
                  setLoadError('');
                }}>
                  {error || loadError}
                </Alert>
              )}

              <Form onSubmit={handleCompare}>
                <Form.Group className="mb-3">
                  <Form.Label>{text.baseArchive}</Form.Label>
                  <Form.Select value={baseArchiveId} onChange={handleBaseArchiveChange} disabled={isLoadingArchives}>
                    <option value="">{text.selectArchive}</option>
                    {archives.map((archive) => (
                      <option key={archive._id} value={archive._id}>{archive.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{text.baseSubject}</Form.Label>
                  <Form.Select
                    value={baseSubjectId}
                    onChange={(event) => {
                      setBaseSubjectId(event.target.value);
                      setComparison(null);
                    }}
                    disabled={!baseArchiveId || loadingSubjectArchiveId === baseArchiveId}
                  >
                    <option value="">{text.selectSubject}</option>
                    {baseSubjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>{subject.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div className="quizify-tool-divider" />

                <Form.Group className="mb-3">
                  <Form.Label>{text.compareArchive}</Form.Label>
                  <Form.Select value={compareArchiveId} onChange={handleCompareArchiveChange} disabled={isLoadingArchives}>
                    <option value="">{text.selectArchive}</option>
                    {archives.map((archive) => (
                      <option key={archive._id} value={archive._id}>{archive.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{text.compareSubject}</Form.Label>
                  <Form.Select
                    value={compareSubjectId}
                    onChange={(event) => {
                      setCompareSubjectId(event.target.value);
                      setComparison(null);
                    }}
                    disabled={!compareArchiveId || loadingSubjectArchiveId === compareArchiveId}
                  >
                    <option value="">{text.selectSubject}</option>
                    {compareSubjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>{subject.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Button type="submit" variant="primary" disabled={!canCompare || isComparing}>
                  {isComparing ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span>{text.comparing}</span>
                    </>
                  ) : (
                    <>
                      <FiRepeat aria-hidden="true" />
                      <span>{text.compare}</span>
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="quizify-tool-panel h-100">
            <Card.Header>
              <Card.Header.Title>
                <h4 className="mb-0">{text.resultTitle}</h4>
              </Card.Header.Title>
            </Card.Header>
            <Card.Body>
              {!comparison ? (
                <div className="quizify-tool-empty">{text.emptyResult}</div>
              ) : (
                <>
                  <div className="quizify-tool-stat-grid is-compare mb-3">
                    <div>
                      <span>{text.matched}</span>
                      <strong>{comparison.summary?.matchedQuestions || 0}</strong>
                    </div>
                    <div>
                      <span>{text.different}</span>
                      <strong>{comparison.summary?.differentQuestions || 0}</strong>
                    </div>
                    <div>
                      <span>{text.onlyBase}</span>
                      <strong>{comparison.summary?.onlyBaseQuestions || 0}</strong>
                    </div>
                    <div>
                      <span>{text.onlyCompare}</span>
                      <strong>{comparison.summary?.onlyCompareQuestions || 0}</strong>
                    </div>
                  </div>

                  <Accordion defaultActiveKey="different" alwaysOpen>
                    <Accordion.Item eventKey="different">
                      <Accordion.Header>{text.differentQuestionsTitle(comparison.different?.length || 0)}</Accordion.Header>
                      <Accordion.Body>
                        {comparison.different?.length ? comparison.different.map((item) => (
                          <div key={`${item.base?.id}-${item.compare?.id}`} className="quizify-compare-question-card">
                            <div className="quizify-compare-question-title">
                              <h5>{item.questionText}</h5>
                              <div>
                                {item.reasons.map((reason) => (
                                  <Badge key={reason} bg={reasonVariant[reason] || 'secondary'}>
                                    {reasonLabel(reason)}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <Row className="g-3">
                              <Col md={6}>
                                <AnswerList title={comparison.baseSubject?.name || text.baseSubject} answers={item.base?.answers || []} />
                              </Col>
                              <Col md={6}>
                                <AnswerList title={comparison.compareSubject?.name || text.compareSubject} answers={item.compare?.answers || []} />
                              </Col>
                            </Row>
                          </div>
                        )) : (
                          <div className="quizify-tool-empty compact">{text.noDifferentQuestions}</div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="base">
                      <Accordion.Header>{text.onlyBaseTitle(comparison.onlyBase?.length || 0)}</Accordion.Header>
                      <Accordion.Body>
                        <QuestionOnlyList questions={comparison.onlyBase || []} emptyText={text.noOnlyBase} answersTitle={text.answers} />
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="compare">
                      <Accordion.Header>{text.onlyCompareTitle(comparison.onlyCompare?.length || 0)}</Accordion.Header>
                      <Accordion.Body>
                        <QuestionOnlyList questions={comparison.onlyCompare || []} emptyText={text.noOnlyCompare} answersTitle={text.answers} />
                      </Accordion.Body>
                    </Accordion.Item>

                    {!!comparison.duplicateWarnings?.length && (
                      <Accordion.Item eventKey="duplicates">
                        <Accordion.Header>{text.duplicateWarningsTitle(comparison.duplicateWarnings.length)}</Accordion.Header>
                        <Accordion.Body>
                          {comparison.duplicateWarnings.map((warning) => (
                            <Alert key={`${warning.questionText}-${warning.baseCount}-${warning.compareCount}`} variant="warning" className="py-2">
                              {text.duplicateWarning(warning.baseCount, warning.compareCount, warning.questionText)}
                            </Alert>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                  </Accordion>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ToolShell>
  );
};

export default SubjectCompareTool;
